import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CheckCircle,
  Eye,
  Compass,
  PlayCircle,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  Brain,
  Database,
  Target,
  ShieldCheck,
} from "lucide-react";

const RevenueIntelligence = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Revenue Intelligence: The Operator's Guide for 2026",
      description:
        "What revenue intelligence is, what it stops at, and why autonomous CRM is the category that actually closes the loop. Written for CROs.",
      author: { "@type": "Organization", name: "NoQ5" },
      publisher: { "@type": "Organization", name: "NoQ5", url: "https://noq5.ai" },
      datePublished: "2026-05-17",
      dateModified: "2026-05-17",
      mainEntityOfPage: "https://noq5.ai/revenue-intelligence",
      mentions: [
        { "@type": "SoftwareApplication", name: "Gong" },
        { "@type": "SoftwareApplication", name: "Clari" },
        { "@type": "SoftwareApplication", name: "Salesloft" },
        { "@type": "SoftwareApplication", name: "Outreach" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is revenue intelligence?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Revenue intelligence is the discipline of using captured sales activity (calls, emails, meetings, CRM events) plus machine learning to produce an objective view of pipeline health, deal risk and forecast accuracy. It replaces gut-feel pipeline reviews with evidence. The first generation of revenue intelligence platforms (Gong, Clari, Salesloft, Outreach) focused on insight. The 2026 generation, autonomous CRM, closes the loop by also executing the work the insight implies.",
          },
        },
        {
          "@type": "Question",
          name: "What is a revenue intelligence platform?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "A revenue intelligence platform ingests sales activity from email, calendar, calls and CRM, then applies AI to score deals, forecast pipeline, detect risk and surface coaching opportunities. Vendors in the category include Clari, Gong, Salesloft, Outreach, Revenue.io and Seismic. NoQ5 sits in the next-generation tier: autonomous CRM, where the platform not only produces the intelligence but executes the resulting CRM updates, enrichment and pipeline actions without rep clicks.",
          },
        },
        {
          "@type": "Question",
          name: "How is revenue intelligence different from CRM?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "A CRM stores what reps choose to enter. Revenue intelligence reconstructs what actually happened from the underlying activity. CRM data is sparse and biased. Revenue intelligence data is dense and objective. The two are complementary: CRM as the system of record, revenue intelligence as the system of truth.",
          },
        },
        {
          "@type": "Question",
          name: "What is the difference between revenue intelligence and conversation intelligence?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Conversation intelligence is a subset. It captures and analyzes calls and meetings: transcripts, talk ratios, topic detection, sentiment. Revenue intelligence is broader: it combines conversation data with email, calendar, CRM and pipeline signals to model deal health and forecast accuracy. Conversation intelligence answers 'what happened on the call'. Revenue intelligence answers 'will this deal close'.",
          },
        },
        {
          "@type": "Question",
          name: "What is deal intelligence?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Deal intelligence is the deal-level layer of revenue intelligence: stage age, multi-thread coverage, response latency, buying-committee changes, sentiment shifts and momentum signals on a single opportunity. It tells a manager which deals are real, which are stalled and which are at risk before the forecast call. NoQ5 surfaces deal intelligence automatically on every opportunity, no rep input required.",
          },
        },
        {
          "@type": "Question",
          name: "Does revenue intelligence replace forecasting software?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "It absorbs it. Traditional forecasting software is a spreadsheet-over-CRM workflow with roll-ups and submissions. Modern revenue intelligence produces an evidence-based forecast directly from captured activity, then compares it side by side with the rep call. The gap is the conversation.",
          },
        },
        {
          "@type": "Question",
          name: "Who needs revenue intelligence?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Revenue leaders running pipelines above approximately five million ARR with three or more reps and any deal cycle longer than 30 days. Below that scale, forecast accuracy is a manager-by-spreadsheet problem. Above it, revenue intelligence becomes mandatory infrastructure.",
          },
        },
        {
          "@type": "Question",
          name: "How does NoQ5 fit in the revenue intelligence category?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "NoQ5 is positioned as autonomous CRM: the execution-grade evolution of revenue intelligence. The platform captures activity, builds the evidence layer, produces an objective forecast and deal-risk view, then writes CRM updates, fires alerts and runs follow-up workflows autonomously. Where first-generation revenue intelligence stops at insight, NoQ5 closes the loop.",
          },
        },
      ],
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Revenue Intelligence: The Operator's Guide for 2026 | NoQ5"
        description="What revenue intelligence is, where first-generation platforms stop, and why autonomous CRM is the category that closes the loop. Written for CROs."
        keywords="revenue intelligence, revenue intelligence platform, revenue intelligence software, what is revenue intelligence, revenue intelligence tools, best revenue intelligence platform, revenue intelligence vs CRM, revenue intelligence vs conversation intelligence, deal intelligence, pipeline intelligence, sales intelligence platform, autonomous CRM, Gong alternative, Clari alternative, Salesloft alternative, Outreach alternative, revenue operations platform, ai chief of staff for revenue"
        canonicalUrl="https://noq5.ai/revenue-intelligence"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6">
              CATEGORY GUIDE
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              Revenue Intelligence.<br />
              <span className="text-primary">Insight Was Round One.</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
              The first generation of revenue intelligence platforms taught the market that captured activity beats rep self-reporting. They were right. They also stopped at the dashboard. The 2026 generation closes the loop.
            </p>
            <p className="text-base text-muted-foreground max-w-3xl mx-auto mb-8">
              This is the operator's guide for revenue leaders evaluating the category. What it is, what it solves, where it falls short, and what autonomous CRM does next.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
                <Link to="/trial?scrollTo=demo">Book a Demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/ai-for-cros">AI for CROs</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Definition */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-6">What Is Revenue Intelligence?</h2>
          <p className="text-lg text-muted-foreground mb-4">
            <strong className="text-foreground">Revenue intelligence</strong> is the discipline of combining captured sales activity (calls, emails, meetings, CRM events) with machine learning to produce an objective view of pipeline health, deal risk and forecast accuracy. It replaces gut-feel pipeline reviews with evidence.
          </p>
          <p className="text-lg text-muted-foreground mb-4">
            The category was built on a real insight: reps log a fraction of what actually happens, so any forecast or dashboard built on CRM data alone is fiction. Capture the underlying activity, model it, and the picture sharpens overnight.
          </p>
          <p className="text-lg text-muted-foreground">
            That insight is now table stakes. The question for 2026 is not whether you capture activity, but whether your platform <strong className="text-foreground">acts on what it sees</strong>.
          </p>
        </div>
      </section>

      {/* The 4 layers */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">The 4 Layers of Revenue Intelligence</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Every serious platform in the category covers some combination of these. The differentiator is depth and what gets done with the output.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Database,
                title: "1. Activity Capture",
                desc: "Email, calendar, call and meeting data flowing into a single graph, automatically attributed to the right contact and deal. Without this layer, everything above is guesswork.",
              },
              {
                icon: Brain,
                title: "2. Deal Intelligence",
                desc: "Stage age, multi-thread coverage, response latency, momentum, sentiment and buying-committee changes on each opportunity. Tells you which deals are real before the forecast call.",
              },
              {
                icon: TrendingUp,
                title: "3. Pipeline Intelligence",
                desc: "Coverage, velocity, conversion bands and risk concentration across the whole pipeline. Tells the CRO what the number actually looks like, not what reps want it to look like.",
              },
              {
                icon: BarChart3,
                title: "4. Forecast Intelligence",
                desc: "An evidence-based forecast produced from objective signals, sat side by side with the rep call. The gap is the coaching conversation.",
              },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-6 bg-card rounded-xl border border-border">
                <item.icon className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where first-gen stops */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">Where First-Generation Revenue Intelligence Stops</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            The category was built to give CROs visibility. Visibility is necessary. It is not sufficient.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card p-8 rounded-xl border border-border">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                <h3 className="text-xl font-bold">First-Generation Revenue Intelligence</h3>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                <li>Captures activity, surfaces it on dashboards</li>
                <li>Flags risk, but the rep still has to act</li>
                <li>Produces a forecast, but does not update CRM</li>
                <li>Coaches after the call, not before the next one</li>
                <li>You buy a better mirror</li>
              </ul>
            </div>
            <div className="bg-card p-8 rounded-xl border-2 border-primary/40">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold text-primary">Autonomous CRM (2026)</h3>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Captures activity AND writes back to CRM</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Flags risk AND opens the follow-up workflow</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Produces a forecast AND keeps CRM honest</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Briefs the rep before the next meeting</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> You buy execution</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* NoQ5 Approach */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">How NoQ5 Delivers Revenue Intelligence</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Capture, Understand, Act. The same three layers every revenue intelligence platform talks about, with the third one actually doing something.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Eye, title: "1. Capture", desc: "Every email, meeting and call flows into NoQ5 in real time. Activity is attributed to the right contact, account and deal without rep input." },
              { icon: Compass, title: "2. Understand", desc: "Deal intelligence, pipeline intelligence and forecast intelligence are continuously recomputed. Account graphs and buying committees stay current." },
              { icon: PlayCircle, title: "3. Act", desc: "CRM fields written, alerts fired, briefings drafted, follow-ups scheduled. The work the insight implies actually gets done." },
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border">
                <item.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Evaluation checklist */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-4">Evaluating a Revenue Intelligence Platform: The 7 Questions</h2>
          <p className="text-center text-muted-foreground mb-12">
            Ask every vendor the same seven questions. The answers separate dashboards from execution.
          </p>
          <div className="space-y-4">
            {[
              "Does the platform capture email, calendar AND call activity natively, or does it depend on a third-party recorder?",
              "Does it produce a forecast independent of rep submissions, or does it just roll up what reps type?",
              "When risk is detected, does a workflow open automatically, or does a rep get a notification?",
              "Does it write back to CRM, or read-only?",
              "Is the deal-intelligence layer recomputed continuously or only at sync intervals?",
              "Where is the data stored, and what is the GDPR posture? (EU residency is mandatory for European buyers.)",
              "Can a CRO use it without training reps on a new interface?",
            ].map((q, i) => (
              <div key={i} className="flex gap-4 p-5 bg-card rounded-xl border border-border">
                <Target className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-foreground">{q}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <ShieldCheck className="h-12 w-12 text-primary mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Enterprise-Grade by Default</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            ISO 27001 in progress. AES-256-GCM encryption. EU data residency. Automatic anonymization. The compliance bar revenue leaders require before they touch a new platform.
          </p>
        </div>
      </section>

      {/* Related */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Go Deeper</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Deal Intelligence", link: "/deal-intelligence", desc: "The deal-level layer of revenue intelligence: momentum, multi-thread, risk." },
              { title: "Sales Forecasting Software", link: "/sales-forecasting-software", desc: "Why spreadsheet roll-ups lose to evidence-based forecasts." },
              { title: "Pipeline Management AI", link: "/pipeline-management-ai", desc: "Stalled deal detection and stage-advancement signals." },
              { title: "AI for CROs", link: "/ai-for-cros", desc: "What revenue leaders actually buy when they buy AI." },
              { title: "CRM Agent", link: "/crm-agent", desc: "The autonomous CRM execution layer in detail." },
              { title: "Best AI Sales Assistants 2026", link: "/best-ai-sales-assistants-2026", desc: "Honest comparison across the category." },
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-card p-6 rounded-xl border border-border hover:border-primary/50 transition-colors group">
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">{item.title}</h3>
                <p className="text-muted-foreground text-sm mb-3">{item.desc}</p>
                <span className="text-primary text-sm font-medium flex items-center gap-1">Read <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">See Revenue Intelligence That Closes the Loop.</h2>
          <p className="text-xl opacity-90 mb-8">
            A 30 minute working session with the founder. Bring your pipeline, leave with a baseline of what autonomous CRM does to forecast accuracy.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/trial?scrollTo=demo">Book a Demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12">Revenue Intelligence: FAQ</h2>
          <div className="space-y-6">
            {[
              { q: "What is revenue intelligence?", a: "The discipline of combining captured sales activity with machine learning to produce an objective view of pipeline health, deal risk and forecast accuracy. The mature form is autonomous CRM, which acts on the intelligence rather than just displaying it." },
              { q: "What is a revenue intelligence platform?", a: "Software that ingests activity from email, calendar, calls and CRM, then applies AI to score deals, forecast pipeline, detect risk and surface coaching. Established vendors include Clari, Gong, Salesloft, Outreach. NoQ5 sits in the next tier: autonomous CRM." },
              { q: "How is revenue intelligence different from CRM?", a: "CRM stores what reps type. Revenue intelligence reconstructs what actually happened from the underlying activity. The two are complementary: CRM as system of record, revenue intelligence as system of truth." },
              { q: "Revenue intelligence vs conversation intelligence?", a: "Conversation intelligence is the call-and-meeting subset. Revenue intelligence is broader, combining conversation data with email, calendar, CRM and pipeline signals to model deal health and forecast accuracy." },
              { q: "What is deal intelligence?", a: "The deal-level layer of revenue intelligence: stage age, multi-thread coverage, response latency, momentum and sentiment on each opportunity. Tells managers which deals are real before the forecast call." },
              { q: "Does revenue intelligence replace forecasting software?", a: "It absorbs it. The evidence-based forecast comes out of the captured activity directly, then sits next to the rep call. The gap is the conversation." },
              { q: "Who needs revenue intelligence?", a: "Revenue leaders running pipelines above approximately 5M ARR with 3+ reps and any deal cycle longer than 30 days. Below that scale it is a spreadsheet problem. Above it, infrastructure." },
              { q: "How does NoQ5 fit the category?", a: "NoQ5 is autonomous CRM: the execution-grade evolution of revenue intelligence. Captures activity, builds the evidence layer, produces the objective forecast, then writes CRM, fires alerts and runs follow-ups autonomously." },
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border">
                <h3 className="font-bold text-lg mb-2">{item.q}</h3>
                <p className="text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RevenueIntelligence;
